import { SchemaExplorerAction } from '../actions/schema-explorer/ActionTypes';
import * as Constants from '../constants';
import initialState from '../InitialState';
import { SchemaExplorer } from '../types/SchemaExplorer';
import {
  addNodeToTree,
  changeTreeState,
  convertSchemasToTree,
  convertSchemaToTree,
  deleteNodeFromTree,
  editNodeInTree
} from '../utils/SchemaExplorer';

const schemaExplorerReducer = (
  state: SchemaExplorer = initialState.schemaExplorer,
  action: SchemaExplorerAction
): SchemaExplorer => {
  switch (action.type) {
    case Constants.GET_ROOT_NODES_SCHEMA:
      return {
        ...state,
        fetching: true,
        payload: {
          ...state.payload,
          activeNode: initialState.schemaExplorer.payload.activeNode,
          activeNodeProperties: {
            ...state.payload.activeNodeProperties,
            payload: []
          }
        }
      };
    case Constants.ADD_ROOT_NODE_SCHEMA:
    case Constants.EDIT_ROOT_NODE_SCHEMA:
    case Constants.DELETE_ROOT_NODE_SCHEMA:
    case Constants.ADD_CHILD_NODE_SCHEMA:
    case Constants.EDIT_CHILD_NODE_SCHEMA:
    case Constants.DELETE_CHILD_NODE_SCHEMA:
      return {
        ...state,
        fetching: true
      };
    case Constants.GET_ROOT_NODES_SCHEMA_FAILED:
    case Constants.ADD_ROOT_NODE_SCHEMA_FAILED:
    case Constants.EDIT_ROOT_NODE_SCHEMA_FAILED:
    case Constants.DELETE_ROOT_NODE_SCHEMA_FAILED:
    case Constants.ADD_CHILD_NODE_SCHEMA_FAILED:
    case Constants.EDIT_CHILD_NODE_SCHEMA_FAILED:
    case Constants.DELETE_CHILD_NODE_SCHEMA_FAILED:
      return {
        ...state,
        fetching: false,
        failed: true
      };
    case Constants.GET_ROOT_NODES_SCHEMA_SUCCESS:
      return {
        ...state,
        fetching: false,
        payload: {
          ...state.payload,
          explorerTree: convertSchemasToTree(action.payload.rootNodes, true)
        }
      };
    case Constants.ADD_ROOT_NODE_SCHEMA_SUCCESS:
      return {
        ...state,
        fetching: false,
        payload: {
          ...state.payload,
          explorerTree: [
            ...state.payload.explorerTree,
            convertSchemaToTree(action.payload.rootNode, true)
          ]
        }
      };
    case Constants.EDIT_ROOT_NODE_SCHEMA_SUCCESS:
      const editedRootNodeIndex = state.payload.explorerTree.findIndex(
        n => n.vmid === action.payload.rootNode.vmid
      );
      return {
        ...state,
        fetching: false,
        payload: {
          ...state.payload,
          explorerTree: [
            ...state.payload.explorerTree.slice(0, editedRootNodeIndex),
            convertSchemaToTree(action.payload.rootNode, true),
            ...state.payload.explorerTree.slice(editedRootNodeIndex + 1)
          ]
        }
      };
    case Constants.DELETE_ROOT_NODE_SCHEMA_SUCCESS:
      return {
        ...state,
        fetching: false,
        payload: {
          ...state.payload,
          explorerTree: state.payload.explorerTree.filter(
            n => n.vmid !== action.payload.vmid
          )
        }
      };
    case Constants.ADD_CHILD_NODE_SCHEMA_SUCCESS:
      return {
        ...state,
        fetching: false,
        payload: {
          ...state.payload,
          explorerTree: addNodeToTree(
            state.payload.explorerTree,
            action.payload.childNode
          )
        }
      };
    case Constants.EDIT_CHILD_NODE_SCHEMA_SUCCESS:
      return {
        ...state,
        fetching: false,
        payload: {
          ...state.payload,
          explorerTree: editNodeInTree(
            state.payload.explorerTree,
            action.payload.childNode
          )
        }
      };
    case Constants.DELETE_CHILD_NODE_SCHEMA_SUCCESS:
      return {
        ...state,
        fetching: false,
        payload: {
          ...state.payload,
          explorerTree: deleteNodeFromTree(
            state.payload.explorerTree,
            action.payload.vmid
          )
        }
      };
    case Constants.COLLAPSE_SCHEMA_NODE:
    case Constants.GET_CHILD_NODES_SCHEMA:
      return {
        ...state,
        payload: {
          ...state.payload,
          explorerTree: changeTreeState(
            state.payload.explorerTree,
            action.payload.vmid
          )
        }
      };
    case Constants.GET_CHILD_NODES_SCHEMA_FAILED:
      return {
        ...state,
        failed: true
      };
    case Constants.GET_CHILD_NODES_SCHEMA_SUCCESS:
      return {
        ...state,
        payload: {
          ...state.payload,
          explorerTree: changeTreeState(
            state.payload.explorerTree,
            action.payload.parentVmid,
            convertSchemasToTree(action.payload.childNodes, false)
          )
        }
      };
    case Constants.SELECT_SCHEMA_NODE:
      return {
        ...state,
        payload: {
          ...state.payload,
          activeNode: action.payload.node
        }
      };
    case Constants.GET_SCHEMA_NODE_PROPERTIES:
    case Constants.ADD_PROPERTY_SCHEMA:
    case Constants.EDIT_PROPERTY_SCHEMA:
      return {
        ...state,
        payload: {
          ...state.payload,
          activeNodeProperties: {
            ...state.payload.activeNodeProperties,
            fetching: true
          }
        }
      };
    case Constants.GET_SCHEMA_NODE_PROPERTIES_FAILED:
    case Constants.ADD_PROPERTY_SCHEMA_FAILED:
    case Constants.EDIT_PROPERTY_SCHEMA_FAILED:
      return {
        ...state,
        payload: {
          ...state.payload,
          activeNodeProperties: {
            ...state.payload.activeNodeProperties,
            fetching: false,
            failed: true
          }
        }
      };
    case Constants.GET_SCHEMA_NODE_PROPERTIES_SUCCESS:
      return {
        ...state,
        payload: {
          ...state.payload,
          activeNodeProperties: {
            ...state.payload.activeNodeProperties,
            fetching: false,
            payload: action.payload.properties
          }
        }
      };
    case Constants.ADD_PROPERTY_SCHEMA_SUCCESS:
      return {
        ...state,
        payload: {
          ...state.payload,
          activeNodeProperties: {
            ...state.payload.activeNodeProperties,
            fetching: false,
            payload: [
              ...state.payload.activeNodeProperties.payload,
              action.payload.property
            ]
          }
        }
      };
    case Constants.EDIT_PROPERTY_SCHEMA_SUCCESS:
      const editPropertyIndex = state.payload.activeNodeProperties.payload.findIndex(
        p => p.vmid === action.payload.property.vmid
      );
      return {
        ...state,
        payload: {
          ...state.payload,
          activeNodeProperties: {
            ...state.payload.activeNodeProperties,
            fetching: false,
            payload: [
              ...state.payload.activeNodeProperties.payload.slice(
                0,
                editPropertyIndex
              ),
              action.payload.property,
              ...state.payload.activeNodeProperties.payload.slice(
                editPropertyIndex + 1
              )
            ]
          }
        }
      };
    default:
      return state;
  }
};

export default schemaExplorerReducer;
